import {
  InviteUsersMutation,
  InviteUsersMutationVariables,
  UserRoleEnum,
} from "@/__generated__/graphql";
import { INVITE_USERS_MUTATION } from "@/components/SettingsSidepanel/settings.graphql.ts";
import { InviteUsersProps } from "@/components/SettingsSidepanel/settings.types.ts";
import SettingsSheetHeader from "@/components/SettingsSidepanel/SettingsSheetHeader.tsx";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useUserContext } from "@/contexts/UserContextProvider";
import { USERS_QUERY } from "@/graphql/queries/common/UsersQuery.ts";
import { USER_ROLE_ACCESS } from "@/helpers/constants";
import { isDefaultVendor } from "@/helpers/userContext";
import { cn } from "@/lib/utils";
import { useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { Field, FieldProps, Form, Formik } from "formik";
import { Info, UserRound, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";

const INITIAL_VALUES = {
  emails: "",
  role: UserRoleEnum.Collaborator,
  primaryOfficeId: null,
  additionalOfficeIds: [],
};

interface FormValues {
  emails: string;
  role: UserRoleEnum;
  primaryOfficeId: string | null;
  additionalOfficeIds: string[];
}
const schema = z.object({
  emails: z
    .string({ required_error: "Email(s) are required." })
    .nonempty("Email(s) are required."),
  role: z.nativeEnum(UserRoleEnum, {
    errorMap: () => ({ message: "Role is required." }),
  }),
});

const InviteUsers: React.FC<InviteUsersProps> = ({
  onRequestClose,
  onBack,
  office,
  offices,
}) => {
  const { currentUser, role } = useUserContext();
  const [officeSearch, setOfficeSearch] = useState<string>("");

  const isDefaultVendorProfile = isDefaultVendor(role!, currentUser!);

  const [inviteUsersMutation] = useMutation<
    InviteUsersMutation,
    InviteUsersMutationVariables
  >(INVITE_USERS_MUTATION, {
    refetchQueries: [
      {
        query: USERS_QUERY,
      },
    ],
  });

  const getAdditionalOfficeOptions = (primaryOfficeId: string) => {
    const companyId = offices.find((o) => o.id === primaryOfficeId)?.company.id;
    if (!companyId) return [];

    return offices.filter(
      (o) => o.company.id === companyId && o.id !== primaryOfficeId,
    );
  };

  return (
    <>
      <SettingsSheetHeader
        title="Invite Users"
        onRequestClose={onRequestClose}
        onBack={onBack}
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 p-4 rounded-2xl border border-accent-foreground text-sm">
          {!isDefaultVendorProfile && (
            <div className="flex items-center bg-[#FFAB4D33] sticky top-0 p-4 rounded-2xl space-x-2 z-10">
              <div className="bg-yellow-600 p-2 rounded-full">
                <Info className="size-5" />
              </div>
              <p className="text-service-3d-tracking text-sm">
                Users will be invited to {office.name}
              </p>
            </div>
          )}

          <Formik<FormValues>
            initialValues={INITIAL_VALUES}
            validationSchema={toFormikValidationSchema(schema)}
            onSubmit={async (
              values,
              { setStatus, setSubmitting, resetForm, setFieldError },
            ) => {
              const emails = values.emails
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean);

              const invalidEmail = emails.find(
                (email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
              );
              if (emails.length === 0 || invalidEmail) {
                setStatus({ formError: "Please enter valid email addresses." });
                setSubmitting(false);
                return;
              }

              try {
                if (isDefaultVendorProfile && !values.primaryOfficeId) {
                  setFieldError(
                    "primaryOfficeId",
                    "Primary office is required.",
                  );
                  setSubmitting(false);
                  return;
                }
                const result = await inviteUsersMutation({
                  variables: {
                    emails,
                    role: values.role,
                    primaryOfficeId: values.primaryOfficeId,
                    additionalOfficeIds: values.additionalOfficeIds,
                  },
                });
                if (result && result.data && result.data.inviteUsers) {
                  const { success, message } = result.data.inviteUsers;
                  if (success) {
                    toast.success(
                      `${emails.length} user${emails.length > 1 ? "s" : ""} invited successfully!`,
                    );
                    resetForm();
                  } else {
                    setStatus({ fromError: message });
                    toast.error(message);
                  }
                }
              } catch (err) {
                const e = err as string;
                captureException(err);
                setStatus({ formError: err });
                toast.error(e);
              } finally {
                setSubmitting(false);
                setOfficeSearch("");
              }
            }}
          >
            {({
              isSubmitting,
              isValid,
              values,
              status,
              setFieldValue,
              dirty,
              errors,
            }) => (
              <Form className="space-y-5">
                <div className="space-y-1">
                  <p>Emails:</p>
                  <Field name="emails">
                    {({ field }: FieldProps) => (
                      <Textarea
                        {...field}
                        value={values.emails}
                        placeholder="Enter Multiple addresses separated by commas"
                        className={cn(
                          "w-full rounded-lg text-sm !ring-0 !outline-none focus:!border-input bg-accent dark:bg-accent",
                        )}
                      />
                    )}
                  </Field>
                  {status?.formError && (
                    <div className="text-xs text-destructive">
                      {status.formError}
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <p>Role:</p>
                  <RadioGroup
                    name="role"
                    value={values.role}
                    onValueChange={(val) => setFieldValue("role", val)}
                    className="flex flex-col gap-5 bg-button p-5"
                  >
                    {Object.values(UserRoleEnum).map((role) => (
                      <label
                        key={role}
                        htmlFor={role}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <RadioGroupItem value={role} id={role} />
                        <div>
                          <p>{role.toUpperCase()}</p>
                          <p className="text-xs text-muted-foreground">
                            {USER_ROLE_ACCESS[role]}
                          </p>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                {isDefaultVendorProfile && (
                  <>
                    <div className="space-y-1">
                      <p>Primary Office:</p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 pl-2 w-5">
                          <UserRound size={14} />
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button className="flex-1 border justify-between text-secondary-foreground font-normal">
                              {values.primaryOfficeId
                                ? offices.find(
                                    (o) => o.id === values.primaryOfficeId,
                                  )?.name
                                : "Select Office"}
                              <ChevronDown />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent
                            className="text-secondary-foreground max-h-44"
                            style={{
                              width: "var(--radix-dropdown-menu-trigger-width)",
                            }}
                            align="end"
                          >
                            <div className="p-2">
                              <input
                                type="text"
                                placeholder="Search office..."
                                className="office-search-input w-full border rounded px-2 py-1 text-sm"
                                value={officeSearch}
                                onChange={(e) =>
                                  setOfficeSearch(e.target.value)
                                }
                                onKeyDownCapture={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                onKeyUpCapture={(e) => e.stopPropagation()}
                                onKeyUp={(e) => e.stopPropagation()}
                              />
                            </div>

                            {[...(offices || [])]
                              .filter((office) =>
                                office.name
                                  .toLowerCase()
                                  .includes(officeSearch.toLowerCase()),
                              )
                              .sort((a, b) =>
                                (a.name || "").localeCompare(
                                  b.name || "",
                                  undefined,
                                  {
                                    sensitivity: "base",
                                  },
                                ),
                              )
                              .map((office) => (
                                <DropdownMenuItem
                                  key={office.id}
                                  className={cn(
                                    "cursor-pointer flex items-center gap-2",
                                    values.primaryOfficeId === office.id &&
                                      "bg-accent-foreground",
                                  )}
                                  onClick={() => {
                                    setFieldValue("primaryOfficeId", office.id);
                                  }}
                                >
                                  {office.name}
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {errors && errors.primaryOfficeId && (
                        <div className="text-xs text-destructive">
                          {errors.primaryOfficeId}
                        </div>
                      )}
                    </div>
                    {values.primaryOfficeId &&
                      getAdditionalOfficeOptions(values.primaryOfficeId)
                        .length > 0 && (
                        <div className="space-y-1">
                          <p>Additional Office(s):</p>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 pl-2 w-5">
                              <UserRound size={14} />
                            </div>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button className="flex-1 border justify-between text-secondary-foreground font-normal">
                                  {values.additionalOfficeIds.length > 0
                                    ? `${values.additionalOfficeIds.length} Office(s) Selected`
                                    : "Select Offices"}
                                  <ChevronDown />
                                </Button>
                              </DropdownMenuTrigger>

                              <DropdownMenuContent
                                className="text-secondary-foreground max-h-44"
                                style={{
                                  width:
                                    "var(--radix-dropdown-menu-trigger-width)",
                                }}
                                align="end"
                              >
                                {getAdditionalOfficeOptions(
                                  values.primaryOfficeId,
                                )
                                  .sort((a, b) =>
                                    (a.name || "").localeCompare(
                                      b.name || "",
                                      undefined,
                                      {
                                        sensitivity: "base",
                                      },
                                    ),
                                  )
                                  .map((office) => (
                                    <DropdownMenuItem
                                      key={office.id}
                                      className={cn(
                                        "cursor-pointer flex items-center gap-2",
                                        values.primaryOfficeId === office.id &&
                                          "bg-accent-foreground",
                                      )}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (
                                          values.additionalOfficeIds.includes(
                                            office.id,
                                          )
                                        ) {
                                          setFieldValue(
                                            "additionalOfficeIds",
                                            values.additionalOfficeIds.filter(
                                              (id) => id !== office.id,
                                            ),
                                          );
                                        } else {
                                          setFieldValue("additionalOfficeIds", [
                                            ...values.additionalOfficeIds,
                                            office.id,
                                          ]);
                                        }
                                      }}
                                    >
                                      <Checkbox
                                        checked={values.additionalOfficeIds.includes(
                                          office.id,
                                        )}
                                      />
                                      {office.name}
                                    </DropdownMenuItem>
                                  ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      )}
                  </>
                )}

                <div className="flex flex-row-reverse">
                  <Button
                    type="submit"
                    variant="paint"
                    disabled={isSubmitting || !isValid || !dirty}
                  >
                    Invite
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};
export default InviteUsers;
